import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from "react";

const DashboardCacheContext = createContext(null);

const STORAGE_KEY = "dash_cache_v1"; // dashboard cache only

export function DashboardCacheProvider({ children }) {
	// hydrate once from sessionStorage
	const [store, setStore] = useState(() => {
		try {
			const raw = sessionStorage.getItem(STORAGE_KEY);
			return raw ? JSON.parse(raw) : {};
		} catch {
			return {};
		}
	});

	// persist every change (sessionStorage clears on tab close)
	useEffect(() => {
		try {
			sessionStorage.setItem(STORAGE_KEY, JSON.stringify(store));
		} catch {
			// ignore quota/serialization errors
		}
	}, [store]);

	const get = useCallback(
		(key) => {
			return store?.[key] ?? null;
		},
		[store]
	);

	const set = useCallback((key, data) => {
		setStore((prev) => ({
			...(prev || {}),
			[key]: { ts: Date.now(), data }
		}));
	}, []);

	// ✅ clear ONLY dashboard cache
	const clear = useCallback(() => {
		setStore({});
		try {
			sessionStorage.removeItem(STORAGE_KEY);
		} catch {}
	}, []);

	const value = useMemo(() => ({ get, set, clear }), [get, set, clear]);

	return (
		<DashboardCacheContext.Provider value={value}>
			{children}
		</DashboardCacheContext.Provider>
	);
}

export function useDashboardCache() {
	const ctx = useContext(DashboardCacheContext);
	if (!ctx)
		throw new Error(
			"useDashboardCache must be used inside DashboardCacheProvider"
		);
	return ctx;
}
